import { useCallback, useEffect, useState } from "react";
import { Copy, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg, brl, fmtDate } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const NewDeposit = ({ onCreated }) => {
  const api = useApi();
  const [f, setF] = useState({ amount: "", description: "", customer_email: "" });
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { amount: parseFloat(String(f.amount).replace(",", ".")), description: f.description || "Depósito", origin_url: window.location.origin };
      if (f.customer_email) body.customer_email = f.customer_email;
      const { data } = await api.post("/admin/deposits", body);
      setLink(data.checkout_url);
      toast.success("Link de pagamento criado");
      onCreated();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card space-y-4" data-testid="new-deposit-form">
      <h2 className="font-heading text-lg font-bold">Gerar link de pagamento</h2>
      <div className="grid gap-4 sm:grid-cols-3">
        <div><Label className="caption">Valor (R$)</Label><Input className="field mt-1" inputMode="decimal" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="100,00" data-testid="deposit-amount-input" /></div>
        <div><Label className="caption">Descrição</Label><Input className="field mt-1" maxLength={140} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Depósito" data-testid="deposit-description-input" /></div>
        <div><Label className="caption">E-mail do pagador (opcional)</Label><Input className="field mt-1" type="email" value={f.customer_email} onChange={(e) => setF({ ...f, customer_email: e.target.value })} data-testid="deposit-email-input" /></div>
      </div>
      <Button type="submit" className="btn-primary" disabled={busy} data-testid="create-payment-link-button">{busy ? "Gerando..." : "Gerar link (taxa 0%)"}</Button>
      {link && (
        <div className="code-block flex items-center gap-2" data-testid="payment-link-result">
          <span className="min-w-0 flex-1 truncate">{link}</span>
          <Button type="button" size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copiado"); }} data-testid="copy-payment-link-button"><Copy size={14} /></Button>
          <a href={link} target="_blank" rel="noreferrer" data-testid="open-payment-link"><ExternalLink size={14} /></a>
        </div>
      )}
    </form>
  );
};

export default function Deposits() {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const load = useCallback(() => {
    api.get("/admin/transactions", { params: { type: "deposit", ...(status && { status }) } }).then((r) => setRows(r.data)).catch(() => {});
  }, [api, status]);
  useEffect(() => { load(); }, [load]);

  const refresh = async (id) => {
    try { await api.post(`/admin/transactions/${id}/refresh`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-8 fade-up">
      <div><p className="caption">Entradas</p><h1 className="font-heading text-4xl font-extrabold sm:text-5xl">Recebimentos</h1></div>
      <NewDeposit onCreated={load} />
      <div className="card">
        <div className="mb-4 flex flex-wrap gap-2">
          {[["", "Todos"], ["paid", "Pagos"], ["pending", "Pendentes"], ["expired", "Expirados"]].map(([v, l]) => (
            <button key={v} onClick={() => setStatus(v)} data-testid={`deposit-filter-${v || "all"}`} className={`chip ${status === v ? "chip-active" : ""}`}>{l}</button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="tx-table" data-testid="deposits-table">
            <thead><tr><th>Descrição</th><th>Origem</th><th>Valor</th><th>Taxa</th><th>Status</th><th>Data</th><th></th></tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-500">Nenhum recebimento.</td></tr>}
              {rows.map((t) => (
                <tr key={t.id} data-testid={`deposit-row-${t.id}`}>
                  <td className="max-w-[240px] truncate">{t.description}{t.reference ? <span className="ml-1 text-xs text-slate-500">#{t.reference}</span> : null}</td>
                  <td className="text-xs uppercase text-slate-400">{t.source}</td>
                  <td className="font-mono">{brl(t.amount)}</td>
                  <td className="font-mono text-emerald-400">R$ 0,00</td>
                  <td><StatusBadge status={t.status} /></td>
                  <td className="whitespace-nowrap text-xs text-slate-400">{fmtDate(t.created_at)}</td>
                  <td className="whitespace-nowrap text-right">
                    {t.status === "pending" && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => refresh(t.id)} data-testid={`deposit-refresh-${t.id}`}><RefreshCw size={14} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(t.checkout_url); toast.success("Link copiado"); }} data-testid={`deposit-copy-${t.id}`}><Copy size={14} /></Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
